import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { IResidentTableRow } from '@/types/admin/residentList';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';
import { capitalizeFirstLetter } from '@/utils/admin/utils';
import { getLeaseStatusColor } from '@/utils/admin/residentsList/status';
import { formatDateShort } from '@/utils/admin/date';

interface ResidentTableRowProps {
  resident: IResidentTableRow;
  onPress: (resident: IResidentTableRow) => void;
  isDesktopView?: boolean;
}

export const ResidentTableRow: React.FC<ResidentTableRowProps> = ({ 
  resident, 
  onPress,
  isDesktopView = false
}) => {
  // Use shared helpers for status color and date formatting

  return (
    <TouchableOpacity 
      style={styles.container} 
      onPress={() => onPress(resident)}
      activeOpacity={0.7}
    >
      {/* Main content - two aligned rows */}
      <View style={styles.mainContent}>
        {/* First row: Name and Customer Type */}
        <View style={styles.topRow}>
          <Text style={styles.nameText} numberOfLines={1}>
            {resident.name}
          </Text>
          <Text style={styles.customerTypeText} numberOfLines={1}>
            {capitalizeFirstLetter(resident.customer_type)}
          </Text>
        </View>
        
        {/* Second row: Lease Status and Autopay */}
        <View style={styles.bottomRow}>
          <View style={styles.statusBadge}>
            <Text style={[styles.statusText, {color: getLeaseStatusColor(resident.lease_status)}]}>
              {capitalizeFirstLetter(resident.lease_status)}
            </Text>
          </View>
          <View style={styles.autopayContainer}>
            <Text style={styles.autopayText}>Autopay: </Text>
            {resident.autopay ? (
              <SVG_COMPONENTS.CHECK_CIRCLE width={12} height={12} color="#4CAF50" />
            ) : (
              <SVG_COMPONENTS.X_CIRCLE width={12} height={12} color="#F44336" />
            )}
          </View>
        </View>
      </View>

      {/* Desktop additional info - conditionally visible */}
      {isDesktopView && (
        <View style={styles.additionalInfo}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Unit:</Text>
            <Text style={styles.infoValue}>{resident.unit || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Last Login:</Text>
            <Text style={styles.infoValue}>{formatDateShort(resident.last_login)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Last Payment:</Text>
            <Text style={styles.infoValue}>{formatDateShort(resident.last_payment)}</Text>
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderBottomWidth: 2,
    borderBottomColor: ADMIN_COLORS.DATA_TABLE.BORDER,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  mainContent: {
    flex: 1,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  nameText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
    flex: 1,
    marginRight: 12,
  },
  statusBadge: {
    paddingVertical: 2,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  customerTypeText: {
    fontSize: 14,
    color: '#757575',
    textAlign: 'right',
  },
  autopayContainer: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    paddingVertical: 2,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  autopayText: {
    fontSize: 12,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
  additionalInfo: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F5F5F5',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  infoLabel: {
    fontSize: 12,
    color: '#757575',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: 12,
    color: '#212121',
  },
});
